import { useCallback, useMemo } from 'react';

import map from 'lodash/map';
import isArray from 'lodash/isArray';
import { TextFieldVariants } from '@mui/material';

import { buildHelperText } from 'src/utils/components';
import AutoCompleteField from 'src/components/AutoCompleteField';

import type { DynamicOptionType } from 'src/types/DynamicField/DynamicField';

interface MultiSelectFieldProps {
  error?: boolean;
  helperText?: string;
  field: UCM.DynamicFieldType;
  value: ({ options?: Array<string> } & Array<string>) | undefined;
  onChange: (
    field: UCM.DynamicFieldType,
    { options }: { options: Array<string> | string },
  ) => void;
  variant?: TextFieldVariants;
  size?: 'small' | 'medium';
  disabled?: boolean;
  multiple?: boolean;
}

export default function MultiSelectField({
  field,
  value,
  onChange,
  error,
  helperText = '',
  variant,
  size,
  disabled,
  multiple = true,
  ...others
}: MultiSelectFieldProps) {
  const selectedOption = useMemo(() => {
    const values = value?.options || value;
    if (!values) return [];

    const options = field?.options || field;
    return options?.filter((option) => values?.includes(option.value));
  }, [field, value]);

  const handleChange = useCallback(
    (values: Array<DynamicOptionType> | DynamicOptionType | null) => {
      if (!values) {
        return;
      }
      const options = isArray(values)
        ? map(values, (option) => option.value)
        : values.value;

      onChange(field, { options });
    },
    [onChange, field],
  );

  return (
    <AutoCompleteField
      {...others}
      multiple={multiple}
      label={field?.label}
      value={selectedOption}
      options={field?.options ?? []}
      onChange={handleChange}
      helperText={buildHelperText(field?.description, helperText)}
      variant={variant}
      size={size}
      disabled={disabled}
      error={error}
    />
  );
}
